/**
 * PresenceCursors — live remote-peer cursors rendered inside the React Flow pane.
 *
 * Peer cursor positions are stored in FLOW coordinates (so they mean the same
 * point on the diagram for everyone regardless of each viewer's pan/zoom). Like
 * HelperLines, this reads the current viewport transform [x, y, zoom] from the
 * React Flow store and converts flow → screen so cursors track panning and
 * zooming. The overlay is non-interactive (pointerEvents: none).
 */
import { useStore } from '@xyflow/react'
import type { PresencePeer } from '../../collab/presence'

export interface PresenceCursorsProps {
  peers: PresencePeer[]
}

export default function PresenceCursors({ peers }: PresenceCursorsProps) {
  const transform = useStore((s) => s.transform)
  const [tx, ty, zoom] = transform

  const withCursor = peers.filter((p) => p.cursor)
  if (withCursor.length === 0) return null

  return (
    <div
      data-presence-layer
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1000,
        overflow: 'hidden',
      }}
    >
      {withCursor.map((p) => {
        // flow -> screen: screen = flow * zoom + translate
        const screenX = p.cursor!.x * zoom + tx
        const screenY = p.cursor!.y * zoom + ty
        return (
          <div
            key={p.clientId}
            data-presence-cursor
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              transform: `translate(${screenX}px, ${screenY}px)`,
              pointerEvents: 'none',
              willChange: 'transform',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 16 16" fill={p.color} aria-hidden>
              <path
                d="M0 0 L0 12 L4 9 L7 15 L9 14 L6 8 L11 8 Z"
                stroke="#fff"
                strokeWidth="1"
              />
            </svg>
            <span
              style={{
                display: 'inline-block',
                marginLeft: 4,
                background: p.color,
                color: '#fff',
                fontSize: 11,
                lineHeight: '14px',
                padding: '1px 5px',
                borderRadius: 4,
                whiteSpace: 'nowrap',
                fontWeight: 600,
              }}
            >
              {p.name}
            </span>
          </div>
        )
      })}
    </div>
  )
}
